import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Environment } from "../utils/Environment";
import { postWithUsername } from "../services/fetchWrapper";
// import Layout from "./Layout";

const apiUrl = Environment.getApiUrl();

const Login: React.FC = () => {
  // const { isLoggedIn, userName, logout } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login } = useAuth(); // Use the login function from AuthContext
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState<boolean>(true);


  useEffect(() => {
    const refresh_token = localStorage.getItem("refresh_token");
    if (refresh_token) {
      navigate("/home");
    }
  }, [navigate]);

  const checkSubscriptionStatus = async () => {
    const token = localStorage.getItem("access_token");
    const subRes = await fetch(`${apiUrl}/validateSubscription`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username }),
    });
    const subData = await subRes.json();
    if (subData.subscriptionStatus === false) {
      setSubscriptionStatus(false);
      return false;
    } else {
      localStorage.setItem("subscription_validated", "true");
      setSubscriptionStatus(true);
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const response = await fetch(`${apiUrl}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ username, password }).toString(),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.message.includes("Invalid username or password")) {
          setError("Invalid username or password");
        } else {
          setError(data.detail || "An unexpected error occurred");
        }
        return;
      }

      // set local storage items null so next buttons will be properly disabled
      [
        "tablesSaved",
        "diamScoreDone",
        "cofScoresDone",
        "wetlandScoreDone",
        "railScoreDone",
        "roadScoreDone",
        "costColumnsConfirmed",
        "costScoringDataLoaded",
        "costCalculationDone"
      ].forEach(key => localStorage.setItem(key, "false"));

      // clear uploaded file names
      Object.keys(localStorage)
        .filter(key => key.startsWith("uploadedCompareFile_"))
        .forEach(key => localStorage.removeItem(key));

      const { access_token, refresh_token, client_id } = await response.json();

      // Store tokens in localStorage
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("userName", username);
      if (client_id) {
        localStorage.setItem("client_id", client_id);
      }

      // Await subscription check and only proceed if valid
      const isValid = await checkSubscriptionStatus();
      if (!isValid) return;

      // Always use postWithUsername for get-client-data
      const clientDataResponse = await postWithUsername(`${apiUrl}/get-client-data`, username);
      const data = await clientDataResponse.json();
      if (data.job_id) {
        localStorage.setItem("job_id", data.job_id);
      } else {
        localStorage.setItem("job_id", "");
      }

      // Update global auth state using the login function
      login(access_token, username);

      // Navigate to the home page
      navigate("/home");
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    }
  };

  if (!subscriptionStatus) {
    return (
      // <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
      <Box sx={{ width: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main' }}>
        Your subscription has expired. Please renew your subscription to continue using this service.
      </Box>
      // </Layout>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#D9D9D91A"
      }}
    >
      <Box
        sx={{
          width: 400,
          p: 4,
          border: "1px solid #ccc",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          backgroundColor: "#fff",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          mt: -40,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Login to Your Account
        </Typography>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ backgroundColor: "#0373B5" }}
          >
            Login
          </Button>
        </form>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Don't have an account? <a href="/register" style={{ color: "#032A65" }}>Register here</a>
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;